import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useProducts } from '../context/ProductContext';
import ProductCard from '../components/ProductCard';
import { preloadProductImages } from '../utils/ImagePreloader';
import './CategoryPage.css';

const CategoryPage = ({ category: propCategory }) => {
  const { category: paramCategory } = useParams();
  const { products, loading, error, pagination, loadProducts } = useProducts();
  const [currentPage, setCurrentPage] = useState(1);
  
  // Use the prop category if provided, otherwise use the URL parameter
  const categoryToUse = propCategory || paramCategory;
  const decodedCategory = categoryToUse ? decodeURIComponent(categoryToUse) : '';

  // Carregar produtos da categoria quando a página mudar
  useEffect(() => {
    loadProducts(currentPage, 20, decodedCategory);
  }, [currentPage, decodedCategory, loadProducts]);
  
  // Pré-carregar imagens quando os produtos forem carregados
  useEffect(() => {
    if (products && products.length > 0) {
      // Pré-carregar imagens de todos os produtos visíveis
      preloadProductImages(products);
      
      // Se houver mais páginas, pré-carregar a próxima página
      if (currentPage < pagination.totalPages) {
        const nextPage = currentPage + 1;
        // Pré-carregar a próxima página em segundo plano após 2 segundos
        setTimeout(() => {
          loadProducts(nextPage, 20, decodedCategory, null, true); // null para search, true para silent
        }, 2000);
      }
    }
  }, [products, currentPage, pagination.totalPages, decodedCategory]);

  // Função para mudar de página
  const handlePageChange = (page) => {
    if (page !== currentPage) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Renderizar paginação
  const renderPagination = () => {
    const { totalPages } = pagination;
    const pages = [];
    
    // Mostrar no máximo 5 páginas
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, startPage + 4);
    
    if (endPage - startPage < 4) {
      startPage = Math.max(1, endPage - 4);
    }
    
    // Botão para primeira página
    if (startPage > 1) {
      pages.push(
        <button 
          key="first" 
          onClick={() => handlePageChange(1)}
          className="pagination-button"
          aria-label="Primeira página"
        >
          &laquo;
        </button>
      );
    }
    
    // Botões para páginas
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`pagination-button ${i === currentPage ? 'active' : ''}`}
          aria-label={`Página ${i}`}
          aria-current={i === currentPage ? 'page' : undefined}
        >
          {i}
        </button>
      );
    }
    
    // Botão para última página
    if (endPage < totalPages) {
      pages.push(
        <button 
          key="last" 
          onClick={() => handlePageChange(totalPages)}
          className="pagination-button"
          aria-label="Última página"
        >
          &raquo;
        </button>
      );
    }
    
    return (
      <div className="pagination">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="pagination-button"
          aria-label="Página anterior"
        >
          Anterior
        </button>
        
        <div className="pagination-numbers">
          {pages}
        </div>
        
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="pagination-button"
          aria-label="Próxima página"
        >
          Próxima
        </button>
      </div>
    );
  };

  // Renderizar mensagem de carregamento
  if (loading && products.length === 0) {
    return <div className="loading">Carregando produtos...</div>;
  }

  // Renderizar mensagem de erro
  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="container">
      <div className="category-header">
        <div className="category-breadcrumb">
          <Link to="/">Início</Link> &gt; <span>{decodedCategory}</span>
        </div>
        <h1>{decodedCategory}</h1>
        <p>Produtos da categoria {decodedCategory}</p>
      </div>
      
      {products.length === 0 ? (
        <div className="no-products">
          <h2>Nenhum produto encontrado</h2>
          <p>Não encontramos produtos na categoria {decodedCategory}.</p>
          <Link to="/" className="back-button">Voltar para a página inicial</Link>
        </div>
      ) : (
        <>
          <div className="products-grid">
            {products.map((product, index) => (
              <div key={`${product.code}-${index}`} className="product-grid-item">
                <ProductCard product={product} />
              </div>
            ))}
          </div>
          
          {pagination.totalPages > 1 && renderPagination()}
        </>
      )}
    </div>
  );
};

export default CategoryPage;
